<template>
  <div class="o-object-profile">
    <!-- portrait or landscape -->
    <transition name="o-object-profile__fade" mode="out-in">
      <LoadingProfile v-if="humanMadeObject.objectProfile == undefined" />
      <component
        :is="componentType"
        v-else
        :class="{
          'o-object-profile--with-background-color': color ? true : false
        }"
        :style="`--background-color: ${color}`"
        :is-part="isPart"
      >
        <!-- title -->
        <template #title>
          <TitleAndArtist
            v-if="humanMadeObject"
            :human-made-object="humanMadeObject"
            :show-manuscript-links="manuscriptPartsHidden"
          />
        </template>

        <template v-if="showImageViewer" #viewer>
          <!-- iiif viewer -->
          <div class="o-object-profile__viewer">
            <ImageViewer
              v-if="isManuscriptWithParts == false"
              ref="iiifViewer"
              :manifest-url="manifestUrl"
              :is-zoomable="isZoomable"
              :background-color="color"
              :show-thumbnails="mediaItems.length > 1"
              teleport-selector="#thumbnail-carousel"
              :canvas-id="selectedCanvasId"
              :image-metadata="imageMetadata"
              :style="`height: ${viewerHeight}`"
              :filter-visual-items="true"
              @canvas-change="handleCanvasChange"
            />
            <ImageViewer
              v-else
              ref="iiifViewer-bookviewer"
              component-type="sequence"
              :manifest-url="manifestUrl"
              :background-color="color"
              :use-canvas-thumbnails="hasCustomManifest"
              :label-source="sequenceLabelSource"
              :show-choice-menu="hasCustomManifest"
              :thumbnail-request-transformer="
                hasCustomManifest
                  ? galleryThumbnailRequestTransformer
                  : undefined
              "
            />
          </div>

          <ImageRequestAndCopyright
            v-if="isManuscriptWithParts == false && mediaItem"
            :id="indexedId"
            :media-info="mediaItem"
            :manifest-url="manifestUrl"
            :caption-details="captionDetails"
            :orientation="componentType"
            :artist="artistLinks"
            :object-copyright-statement="
              humanMadeObject?.objectProfile?.copyrightStatement
            "
            :open-content="humanMadeObject?.isOpenContent"
          />
        </template>
        <template
          v-else-if="humanMadeObject.isDeaccessioned && mediaItem"
          #fallback
        >
          <!-- if no image available -->
          <ImageResponsive
            :id="extractImageUuid(mediaItem.iiifImageId)"
            ref="imageResponsive"
            class="o-object-profile__deaccessioned-image"
            size="post_xsmall_variable_height"
            :alt="mediaItem.altText"
          >
            {{ deaccessionImageCaption }}
            <span v-if="mediaItem.copyright"
              >Image: {{ mediaItem.copyright }}</span
            >
          </ImageResponsive>
        </template>
        <template v-else-if="isImageUnavailable" #fallback>
          <!-- if no image available -->
          <ResourceUnavailable ref="imageUnavailable" />
        </template>

        <!-- alternate views -->
        <template
          v-if="mediaItems.length > 1 && isManuscriptWithParts == false"
          #altViews
        >
          <!-- image carousel for iiif viewer -->
          <div class="o-object-profile__thumbnail-carousel-wrapper">
            <h2
              ref="heading"
              class="o-object-profile__thumbnail-carousel-header"
            >
              {{ thumbnailCarouselHeading }}
            </h2>
            <h2 id="alternate-views" class="visually-hidden">
              {{ thumbnailCarouselAriaHeading }}
            </h2>

            <div
              id="thumbnail-carousel"
              class="o-object-profile__thumbnail-carousel"
            />
          </div>
        </template>

        <!-- object nunmber  -->
        <template #objectNumber>
          <template v-if="humanMadeObject.isDeaccessioned">
            {{ deaccessionPrefix }}
          </template>
          {{ objectNumber }}
        </template>

        <!-- part of  -->
        <template v-if="isPart" #partOf>
          <!-- if it is a manuscript without parts we add a class to add additional padding -->
          <PartOf
            v-bind="parentObject"
            ref="partOf"
            :class="{
              'a-part-of__no-parts':
                (isManuscriptWithParts == false && showBookViewer) ||
                displayPartsOfSetComponent == false
            }"
          />
        </template>

        <!-- description -->
        <template
          v-if="objectProfile?.description || humanMadeObject?.isDeaccessioned"
          #description
        >
          <div class="o-object-profile__description">
            <div
              v-if="humanMadeObject.isDeaccessioned"
              class="o-object-profile__deaccession-statement"
            >
              <span class="o-object-profile__deaccession-statement-heading">
                {{ deaccessionStatementHeading }}
              </span>
              <div
                v-if="deaccessionStatement || deaccessionStatementLong"
                class="o-object-profile__deaccession-statement-text"
              >
                <RichText
                  v-if="deaccessionStatement"
                  :content="deaccessionStatement"
                />
                <RichText
                  v-if="deaccessionStatementLong"
                  :content="deaccessionStatementLong"
                />
              </div>
            </div>
            <!-- description -->
            <RichText
              v-if="objectProfile?.description"
              ref="description"
              class="o-object-profile__description-text"
              :content="objectProfile?.description"
            />
          </div>
        </template>
      </component>
    </transition>
  </div>
</template>

<script>
import {
  useLocalization,
  breakpointsMixin,
  scrollToHashMixin,
  ImageResponsive,
  ResourceUnavailable,
  RichText
} from "@thegetty-private/getty-ui";
import { ImageViewer } from "@thegetty-private/image-viewer";
import "@thegetty-private/image-viewer/dist/image-viewer.css";
import { routerHelpers, getIndexedId } from "@thegetty-private/rad-app-utils";
import { extractImageUuid } from "@thegetty-private/linked-data.js";
import LoadingProfile from "./loading.vue";
import Landscape from "./landscape.vue";
import Portrait from "./portrait.vue";
import PartOf from "@/components/atoms/partOf/index.vue";
import ImageRequestAndCopyright from "@/components/molecules/imageRequestAndCopyright/index.vue";
import TitleAndArtist from "@/components/organisms/objectProfile/titleAndArtist/index.vue";
import * as colorHelpers from "@/helpers/colorHelpers";
import ObjectProfileAutoHeight from "@/components/organisms/objectProfile/objectProfileAutoHeight.js";

export default {
  name: "ObjectProfile",
  components: {
    ImageRequestAndCopyright,
    ImageResponsive,
    ImageViewer,
    ResourceUnavailable,
    TitleAndArtist,
    Landscape,
    LoadingProfile,
    RichText,
    PartOf,
    Portrait
  },
  mixins: [ObjectProfileAutoHeight, breakpointsMixin, scrollToHashMixin],
  props: {
    /**
     * The HumanMadeObject data from the store
     */
    humanMadeObject: {
      type: Object,
      required: true
    },
    /**
     * Boolean for whether the manuscript parts carousel is hidden
     */
    manuscriptPartsHidden: {
      type: Boolean,
      required: false,
      default: undefined
    },
    /**
     * Whether we're displaying additional parts of a set in the
     * HumanMadeObject
     */
    displayPartsOfSetComponent: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },
  data() {
    return {
      /**
       * The custom background color to use if available
       */
      backgroundColor: null,
      /**
       * Portrait / Landscape, or null
       */
      componentType: "Portrait",
      /**
       *  Selected canvas id for Image Viewer canvas viewer to display
       *
       *  @returns {string}
       */
      selectedCanvasId: undefined,
      /**
       * Translated text for the alternate views aria label
       *
       * @returns {string}
       */
      thumbnailCarouselAriaHeading: this.translatePart(
        "object_page.alternate_views_aria"
      ),
      /**
       * Caption for deaccessioned artwork image
       */
      deaccessionImageCaption: this.translatePart(
        "object_page.deaccessioned_image_caption"
      ),
      /**
       * Deaccessioned object statement heading
       */
      deaccessionStatementHeading: this.translatePart(
        "object_page.deaccession_statement_heading"
      ),
      /**
       * The deaccessioned label prefix
       */
      deaccessionPrefix: this.translatePart(
        "object_page.object_number_deaccession_prefix"
      )
    };
  },
  computed: {
    /**
     * the indexed id
     */
    indexedId() {
      return getIndexedId(this?.humanMadeObject?.id);
    },
    /**
     * returns the object profile section of the pinia data
     *
     * @returns {object}
     */
    objectProfile() {
      return this?.humanMadeObject?.objectProfile;
    },
    /**
     * Whether the human made object is associated with a custom IIIF manifest
     *
     * @returns {boolean}
     */
    hasCustomManifest() {
      return this.humanMadeObject?.containsCustomManifest;
    },
    /**
     * Whether we have a manuscript with parts or not
     *
     * @returns {boolean}
     */
    isManuscriptWithParts() {
      return this.humanMadeObject?.isManuscriptWithParts === true;
    },
    /**
     * The list of artists for the work
     *
     * @returns {array} a list of artist objects
     */
    artistLinks() {
      return this.objectProfile?.artists || [];
    },
    /**
     * Gets the captions for the request form modal
     *
     * @returns {object} - the captions object
     */
    captionDetails() {
      return this.objectProfile.captionDetails || [];
    },
    /**
     * Gets the colors from Elasticseach for an object and returns a GettyUI color for the background
     * that's closest to the 1st dark color
     *
     * @returns {string} - a hex color
     */
    color() {
      // check if custom background color has already been found
      if (this.backgroundColor) {
        return this.backgroundColor;
      }

      let colors = this.objectProfile?.colors;
      if (
        colors == undefined ||
        colors.length == 0 ||
        this.humanMadeObject.isDeaccessioned
      ) {
        return colorHelpers.getDefaultColor();
      }

      // if custom background is found, set variable so we don't get stuck in loop here
      let customColor = colorHelpers.getClosestDarkColor(colors);
      this.setBackgroundColor(customColor);
      return customColor;
    },
    /**
     * Is this object a part of a complex object & has the parent data
     *
     * @returns {boolean}
     */
    isPart() {
      return this.parentObject ? true : false;
    },
    /**
     * Is this object a manuscript
     *
     * @returns {boolean}
     */
    showBookViewer() {
      return this.humanMadeObject?.structure?.isManuscript === true;
    },
    /**
     * Is the image zoomable or not (should we show the IIIF Viewer)
     *
     * @returns {boolean}
     */
    isZoomable() {
      return this?.mediaItem?.isZoomable;
    },
    /**
     * The translated/formatted object number for the object profile
     *
     * @returns {string}
     */
    objectNumber() {
      return this?.objectProfile?.objectNumber;
    },
    /**
     * The complex object parent ES results data
     * transformed for usage in RadImageResponsive
     *
     * @returns {object} - transformed ES result object
     */
    parentObject() {
      return this?.humanMadeObject?.structure?.partOf;
    },
    /**
     * A helper to return the set of visual item data
     *
     * @returns {array}
     */
    mediaItems() {
      return this.objectProfile?.mediaItems || [];
    },
    /**
     * The current visual item
     *
     * @returns {object}
     */
    mediaItem() {
      const _mediaItem = this.mediaItems.find(
        (item) => item.canvasId == this.selectedCanvasId
      );
      return _mediaItem ?? this.mediaItems[0];
    },
    /**
     * The current manifest url
     *
     * @returns {string}
     */
    manifestUrl() {
      return this?.humanMadeObject?.manifestUrl;
    },
    /**
     * Translated text to display as the alternate views heading
     *
     * @returns {string}
     */
    thumbnailCarouselHeading() {
      return this.translatePart("object_page.alternate_views", {
        total: this.mediaItems?.length
      });
    },
    /**
     * Gets the deaccession statement if present
     * @returns {string|undefined}
     */
    deaccessionStatement() {
      return this.objectProfile?.deaccessionStatement;
    },
    /**
     * Gets the deaccession statement if present
     * @returns {string|undefined}
     */
    deaccessionStatementLong() {
      return this.objectProfile?.deaccessionStatementLong;
    },
    /**
     * Returns a map of the extra image metadata from the Visual Item(s)
     *
     * @returns {object}
     */
    imageMetadata() {
      let map = {};
      this.mediaItems.forEach((item) => {
        map[item.canvasId] = item;
      });
      return map;
    },
    hasLoadedManifest() {
      return this.humanMadeObject?.manifestLoaded === true;
    },
    /**
     * Image Viewer sequence viewer label source param
     *
     * @returns {string} supported values ['default','range','all']
     */
    sequenceLabelSource() {
      return this.humanMadeObject?.sequenceLabelSource;
    },
    /**
     * Whether or not to render the image viewer
     * @returns {boolean}
     */
    showImageViewer() {
      return (
        this.hasLoadedManifest &&
        !this.humanMadeObject?.isDeaccessioned &&
        (this.mediaItems.length > 0 || this.isManuscriptWithParts === true)
      );
    },
    /**
     * Whether or not to show the resource unavailable component for the current object
     * @returns {boolean}
     */
    isImageUnavailable() {
      return (
        "manifestUrl" in this.humanMadeObject &&
        this.humanMadeObject.manifestUrl == undefined
      );
    }
  },
  watch: {
    showImageViewer: {
      // initialize selected canvas id when canvases / visual items are loaded
      handler() {
        if (
          this.selectedCanvasId == undefined &&
          this.isManuscriptWithParts === false
        ) {
          this.selectedCanvasId = this.mediaItems[0].canvasId;
        }
      },
      once: true
    }
  },
  methods: {
    /**
     * Transform the thumbnail requests in the ImageViewer Gallery for Bayard
     * @param {t} thumbnail
     *
     * @returns {object}
     */
    galleryThumbnailRequestTransformer(t) {
      if (this.manifestUrl.includes("bayard-custom") == false) {
        return;
      }
      let src = t.id.replace(
        "https://staging-data.aws.getty.edu/media/manifest/bayard-custom/canvas/",
        "https://static.getty.edu/collection-online-20/bayard-manifest/bayard_thumb_"
      );
      // production
      src = src.replace(
        "https://data.getty.edu/media/manifest/bayard-custom/canvas/",
        "https://static.getty.edu/collection-online-20/bayard-manifest/bayard_thumb_"
      );
      return {
        type: "ImageResponsive",
        src: src + ".jpg",
        size: "post_small_variable_height",
        caption: t.label.en.join(" "),
        canvasId: t.id
      };
    },
    /**
     * Sets the backgroundColor data field
     * @param {string} color - the color to use for the background
     */
    setBackgroundColor(color) {
      this.backgroundColor = color;
    },
    /**
     * Extracts the canvas ID from the the full url
     *
     * @param {string} - canvas URL
     *
     * @returns {string}
     */
    extractCanvasUuid(canvasUrl) {
      let uuid = canvasUrl.split("/").pop();
      return uuid?.replace(".json", "");
    },
    /**
     * get the full canvas id from media items
     * @param {string} uuid - the canvas UUID
     *
     * @returns {string}
     */
    getCanvasId(uuid) {
      let _mediaItem =
        this.mediaItems.find((item) => item?.canvasId?.includes(uuid)) ??
        this.mediaItems[0];
      return _mediaItem.canvasId;
    },
    /**
     * Update route with canvas change event
     *
     * @param {event} e - canvas change event
     * @param {boolean} updateRoute - whether to update the route or just return the path (testing)
     */
    handleCanvasChange(e, updateRoute = true) {
      const currentQuery = this.$route.query ?? {};
      // with canvas query on initialization, canvas change fires without a canvas
      if (!e.detail?.canvas && currentQuery.canvas) {
        this.selectedCanvasId = this.getCanvasId(currentQuery.canvas);
        return;
      }

      // if we're on the 1st item, don't update the route
      if (
        e.detail?.canvas == this.mediaItems[0].canvasId &&
        currentQuery.canvas == undefined
      ) {
        return;
      }

      // otherwise update the route
      if (e.detail?.canvas) {
        let canvasId = e.detail?.canvas;
        let selectedMediaItem = this.mediaItems.find(
          (item) => item.canvasId == canvasId
        );

        if (e.detail?.canvas == this.mediaItems[0].canvasId) {
          // skip if clicking on 1st media item
          canvasId = undefined;
        }

        if (selectedMediaItem == undefined) {
          // reset if invalid media item canvas id
          canvasId = undefined;
          this.selectedCanvasId = this.mediaItems[0].canvasId;
        } else {
          this.selectedCanvasId = selectedMediaItem.canvasId;
        }

        const uuid = canvasId ? this.extractCanvasUuid(canvasId) : undefined;

        const path = {
          name: "object",
          query: { ...currentQuery, canvas: uuid }
        };

        if (updateRoute == false) {
          return path;
        }

        routerHelpers.routerReplace(this.$router, path);
        // scroll to top of object profile to see selected image (on non-mobile)
        if (!this.isSmallMinus()) {
          const globalHeaderHeight =
            document.querySelector(".g-header").offsetHeight;
          this.scrollToHash(".o-object-profile", globalHeaderHeight);
        }
        return;
      }
    },
    /**
     * Wrapper for image uuid linked data helper
     * @param {string} - image service url
     * @return {string} - uuid
     */
    extractImageUuid(url) {
      return extractImageUuid(url);
    }
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
